import {
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import SkillDrawer from './SkillDrawer';

function SkillsPage() {
  const documentTitle = useDocumentTitle('Skills');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true', size: 1000 });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .skills()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .skills()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (skill) => {
    setDialog(
      <SkillDrawer
        skill={skill}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Skills');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).skills(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(skill) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Skill?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .skills(skill ? skill.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Skills</Page.Title>
          <Page.Description>
            Skills can be assigned to members and placeholders. You can also filter reports by skills.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('skills.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('skills.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Skill" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column>Name</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((skill) => {
              const { name, isActive, id } = skill;

              return (
                <ListView.Row onClick={() => handleEdit(skill)} key={id} isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListViewActions>
                    <>
                      <ListViewActions.Edit onClick={() => handleEdit(skill)} />

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => (
                          <>
                            <ListViewMenu.Item onClick={() => handleEdit(skill)}>Edit</ListViewMenu.Item>
                            <ListViewMenu.Item
                              onClick={async () => {
                                await handleActiveStatusChange(skill, !isActive);
                                setIsOpen(false);
                              }}>
                              {isActive ? 'Deactivate' : 'Activate'}
                            </ListViewMenu.Item>
                            <ListViewMenu.DeleteItem
                              tooltip="This item is currently in use."
                              onCheckDependencies={async (workspace) =>
                                (await workspace.skills(id).hasDependencies()).data
                              }
                              onClick={() => handleDelete(skill)}>
                              Delete
                            </ListViewMenu.DeleteItem>
                          </>
                        )}
                      </ListViewMenu>
                    </>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Skill" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default SkillsPage;
